/**
 * WooCommerce Products API Integration
 * 
 * Handles product fetching, caching, and rate limiting for WooCommerce REST API.
 * Implements OAuth 1.0a authentication and respects the 200 requests/hour/user limit.
 */

import { Product, ProductParams } from '../../types/ecommerce';
import { PaginatedResponse, WooCommerceAPI } from '../../types/api';
import { createAPIClient, APIClient } from '../client';

/**
 * Cache implementation for product data
 * Respects WooCommerce rate limiting (200 req/hour/user)
 */
class ProductCache {
  private cache = new Map<string, { data: any; expires: number }>();

  private readonly TTL = 300000; // 5 minutes

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item || Date.now() > item.expires) {
      this.cache.delete(key);
      return null;
    }
    return item.data;
  }

  set(key: string, data: any, ttl = this.TTL): void {
    this.cache.set(key, {
      data,
      expires: Date.now() + ttl
    });
  }

  clear(): void {
    this.cache.clear();
  }

  isExpired(key: string): boolean {
    const item = this.cache.get(key);
    return !item || Date.now() > item.expires;
  }
}

/**
 * Rate limiter for WooCommerce API
 * Implements exponential backoff and request queuing
 */
class RateLimiter {
  private requestQueue: Array<() => Promise<any>> = [];

  private requestCount = 0;

  private windowStart = Date.now();

  private readonly WINDOW_SIZE = 3600000; // 1 hour

  private readonly MAX_REQUESTS = 200;

  private isProcessing = false;

  async executeRequest<T>(request: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push(async () => {
        try {
          const result = await request();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });

      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      // Reset window if needed
      if (Date.now() - this.windowStart > this.WINDOW_SIZE) {
        this.requestCount = 0;
        this.windowStart = Date.now();
      }

      // Check rate limit
      if (this.requestCount >= this.MAX_REQUESTS) {
        const waitTime = this.WINDOW_SIZE - (Date.now() - this.windowStart);
        console.warn(`Rate limit reached. Waiting ${waitTime}ms`);
        await this.sleep(waitTime);
        this.requestCount = 0;
        this.windowStart = Date.now();
      }

      const request = this.requestQueue.shift();
      if (request) {
        try {
          await request();
          this.requestCount++;
        } catch (error) {
          console.error('Request failed:', error);
          // Implement exponential backoff for failed requests
          await this.sleep(Math.min(1000 * 2**(this.requestCount % 5), 30000));
        }
      }
    }

    this.isProcessing = false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * WooCommerce Products API Client
 * 
 * Provides methods for fetching and managing WooCommerce products
 * with built-in caching and rate limiting.
 */
export class WooCommerceProductsAPI {
  private client: APIClient;

  private cache = new ProductCache();

  private rateLimiter = new RateLimiter();

  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL;
    this.client = createAPIClient({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Transform WooCommerce API product to internal Product interface
   */
  private transformProduct(wcProduct: WooCommerceAPI.Product): Product {
    return {
      id: wcProduct.id.toString(),
      name: wcProduct.name,
      description: wcProduct.description,
      shortDescription: wcProduct.short_description,
      price: parseFloat(wcProduct.price),
      regularPrice: wcProduct.regular_price ? parseFloat(wcProduct.regular_price) : undefined,
      salePrice: wcProduct.sale_price ? parseFloat(wcProduct.sale_price) : undefined,
      images: wcProduct.images.map(img => ({
        id: img.id.toString(),
        src: img.src,
        alt: img.alt,
        name: img.name
      })),
      categories: wcProduct.categories.map(cat => ({
        id: cat.id.toString(),
        name: cat.name,
        slug: cat.slug
      })),
      inventory: {
        stockQuantity: wcProduct.stock_quantity || 0,
        stockStatus: wcProduct.stock_status as 'instock' | 'outofstock' | 'onbackorder',
        manageStock: wcProduct.manage_stock,
        backordersAllowed: wcProduct.backorders_allowed
      },
      seo: {
        metaTitle: wcProduct.name,
        metaDescription: wcProduct.short_description
      },
      sku: wcProduct.sku,
      weight: wcProduct.weight,
      dimensions: wcProduct.dimensions ? {
        length: wcProduct.dimensions.length,
        width: wcProduct.dimensions.width,
        height: wcProduct.dimensions.height
      } : undefined,
      status: wcProduct.status as 'publish' | 'draft' | 'private',
      type: wcProduct.type as 'simple' | 'grouped' | 'external' | 'variable',
      featured: wcProduct.featured,
      dateCreated: new Date(wcProduct.date_created),
      dateModified: new Date(wcProduct.date_modified)
    };
  }

  /**
   * Fetch products with caching and pagination
   * 
   * @param params - Product filtering and pagination parameters
   * @returns Promise resolving to paginated product list
   */
  async getProducts(params: ProductParams = {}): Promise<PaginatedResponse<Product>> {
    const cacheKey = `products_${JSON.stringify(params)}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && !this.cache.isExpired(cacheKey)) {
      return cached;
    }

    const response = await this.rateLimiter.executeRequest(async () => {
      const queryParams = new URLSearchParams();
      
      // Add search parameters
      if (params.search) queryParams.append('search', params.search);
      if (params.category) queryParams.append('category', params.category);
      if (params.minPrice) queryParams.append('min_price', params.minPrice.toString());
      if (params.maxPrice) queryParams.append('max_price', params.maxPrice.toString());
      if (params.stockStatus) queryParams.append('stock_status', params.stockStatus);
      if (params.featured) queryParams.append('featured', 'true');
      if (params.onSale) queryParams.append('on_sale', 'true');
      
      // Add sorting parameters
      if (params.orderBy) queryParams.append('orderby', params.orderBy);
      if (params.order) queryParams.append('order', params.order);
      
      // Add pagination parameters
      queryParams.append('page', (params.page || 1).toString());
      queryParams.append('per_page', (params.perPage || 10).toString());

      return this.client.get<WooCommerceAPI.Product[]>(`/wc/v3/products?${queryParams.toString()}`);
    });

    // Transform WooCommerce products to internal format
    const transformedProducts = response.data.map(this.transformProduct);

    // Create paginated response
    const paginatedResponse: PaginatedResponse<Product> = {
      data: transformedProducts,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      pagination: {
        currentPage: params.page || 1,
        perPage: params.perPage || 10,
        totalItems: parseInt(response.headers['x-wp-total'] || '0'),
        totalPages: parseInt(response.headers['x-wp-totalpages'] || '0'),
        hasNextPage: (params.page || 1) < parseInt(response.headers['x-wp-totalpages'] || '0'),
        hasPreviousPage: (params.page || 1) > 1
      }
    };

    // Cache the response
    this.cache.set(cacheKey, paginatedResponse);

    return paginatedResponse;
  }

  /**
   * Fetch a single product by ID
   * 
   * @param productId - Product identifier
   * @returns Promise resolving to Product or null if not found
   */
  async getProduct(productId: string): Promise<Product | null> {
    const cacheKey = `product_${productId}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && !this.cache.isExpired(cacheKey)) {
      return cached;
    }

    try {
      const response = await this.rateLimiter.executeRequest(async () => this.client.get<WooCommerceAPI.Product>(`/wc/v3/products/${productId}`));

      const product = this.transformProduct(response.data);
      this.cache.set(cacheKey, product);
      
      return product;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Search products by text query
   * 
   * @param query - Search query string
   * @param options - Additional search options
   * @returns Promise resolving to search results
   */
  async searchProducts(
    query: string, 
    options: Partial<ProductParams> = {}
  ): Promise<PaginatedResponse<Product>> {
    return this.getProducts({
      ...options,
      search: query
    });
  }

  /**
   * Get products by category
   * 
   * @param categorySlug - Category slug
   * @param options - Additional filtering options
   * @returns Promise resolving to category products
   */
  async getProductsByCategory(
    categorySlug: string,
    options: Partial<ProductParams> = {}
  ): Promise<PaginatedResponse<Product>> {
    return this.getProducts({
      ...options,
      category: categorySlug
    });
  }

  /**
   * Get featured products
   * 
   * @param options - Additional filtering options
   * @returns Promise resolving to featured products
   */
  async getFeaturedProducts(
    options: Partial<ProductParams> = {}
  ): Promise<PaginatedResponse<Product>> {
    return this.getProducts({
      ...options,
      featured: true
    });
  }

  /**
   * Get products on sale
   * 
   * @param options - Additional filtering options
   * @returns Promise resolving to sale products
   */
  async getSaleProducts(
    options: Partial<ProductParams> = {}
  ): Promise<PaginatedResponse<Product>> {
    return this.getProducts({
      ...options,
      onSale: true
    });
  }

  /**
   * Get related products for a given product
   * 
   * @param productId - Product identifier
   * @returns Promise resolving to related products
   */
  async getRelatedProducts(productId: string): Promise<Product[]> {
    const cacheKey = `related_${productId}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && !this.cache.isExpired(cacheKey)) {
      return cached;
    }

    try {
      const product = await this.getProduct(productId);
      if (!product) {
        return [];
      }

      // Get products from the same categories
      const categoryProducts = await Promise.all(
        product.categories.map(category => 
          this.getProductsByCategory(category.slug, { perPage: 4 })
        )
      );

      // Flatten and deduplicate
      const relatedProducts = categoryProducts
        .flatMap(response => response.data)
        .filter((p, index, arr) => 
          p.id !== productId && arr.findIndex(item => item.id === p.id) === index
        )
        .slice(0, 8); // Limit to 8 related products

      this.cache.set(cacheKey, relatedProducts);
      return relatedProducts;
    } catch (error) {
      console.error('Error fetching related products:', error);
      return [];
    }
  }

  /**
   * Clear the product cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: (this.cache as any).cache.size,
      keys: Array.from((this.cache as any).cache.keys())
    };
  }
}

// Export singleton instance
export const wooCommerceProductsAPI = new WooCommerceProductsAPI();